/**
 * Approval chains (ADR-0102): levels signed in order by different people — a role, the
 * requester's manager (from the directory) or a person named in the request — with conditions,
 * a quorum, a time limit in working hours or days and escalation.
 *
 * A rule or step with one plain level by role keeps the older flat form (approver_roles, sla,
 * escalate_to), so agents that never use chains keep their saved form (and digest) unchanged.
 */
import type { ApprovalLevel, ApprovalRule, SpecNode } from "./types";

export const MAX_LEVELS = 6;
export const MAX_QUORUM = 10;
export const MAX_WORKING_DAYS = 60;
/** Approver roles offered first; the organisation's own roles come from the roles picker. */
export const COMMON_APPROVER_ROLES: readonly string[] = ["approver", "finance-approver", "reviewer", "case-supervisor"];

type Flat = { approver_roles?: string[]; sla?: string | null; sla_days?: number | null; escalate_to?: string[] | null; levels?: ApprovalLevel[] | null };

/** The levels of a rule or approval step (one level from the flat fields when it has none). */
export function levelsOf(x: Flat): ApprovalLevel[] {
  if (x.levels?.length) return x.levels;
  return [
    {
      approvers: "roles",
      approver_roles: x.approver_roles?.length ? x.approver_roles : ["approver"],
      ...(x.escalate_to?.length ? { escalate_to: x.escalate_to } : {}),
      ...(x.sla_days ? { sla_days: x.sla_days } : x.sla ? { sla: x.sla } : {}),
    },
  ];
}

/** A level that the flat form can hold: one plain level by role. */
function plain(levels: ApprovalLevel[]): boolean {
  if (levels.length !== 1) return false;
  const lv = levels[0] as ApprovalLevel;
  return (lv.approvers ?? "roles") === "roles" && !lv.when && !lv.label && (lv.quorum ?? 1) === 1 && !lv.manager_of && !lv.person;
}

/** The time limit of a single plain level, kept on the rule or step itself. */
function slaOf(lv: ApprovalLevel, fallback: string): { sla: string; sla_days?: number } {
  return lv.sla_days ? { sla: fallback, sla_days: lv.sla_days } : { sla: lv.sla ?? fallback };
}

/** The rule with these levels (flat when one plain level by role). */
export function withRuleLevels(rule: ApprovalRule, levels: ApprovalLevel[]): ApprovalRule {
  const { levels: _old, approver_roles: _r, escalate_to: _e, sla_days: _d, ...rest } = rule;
  if (plain(levels)) {
    const lv = levels[0] as ApprovalLevel;
    return { ...rest, ...slaOf(lv, rule.sla ?? "PT4H"), approver_roles: lv.approver_roles?.length ? lv.approver_roles : ["approver"], ...(lv.escalate_to?.length ? { escalate_to: lv.escalate_to } : {}) };
  }
  return { ...rest, approver_roles: ["approver"], levels: levels.map(clean) };
}

/** The approval step's fields for these levels (flat when one plain level by role). */
export function stepLevelsPatch(levels: ApprovalLevel[], sla = "PT4H"): Partial<SpecNode> {
  if (plain(levels)) {
    const lv = levels[0] as ApprovalLevel;
    const time = slaOf(lv, sla);
    return { approver_roles: lv.approver_roles?.length ? lv.approver_roles : ["approver"], escalate_to: lv.escalate_to?.length ? lv.escalate_to : undefined, levels: undefined, sla: time.sla, sla_days: time.sla_days };
  }
  return { approver_roles: ["approver"], escalate_to: undefined, levels: levels.map(clean), sla_days: undefined };
}

/** Drop what a level does not use (no empty or null keys in the saved form). */
export function clean(lv: ApprovalLevel): ApprovalLevel {
  const kind = lv.approvers ?? "roles";
  const out: ApprovalLevel = { approvers: kind };
  if (lv.label && (lv.label.en || lv.label.ar)) out.label = lv.label;
  if (lv.when) out.when = lv.when;
  if (kind === "roles") out.approver_roles = lv.approver_roles ?? [];
  if (kind === "manager" && lv.manager_of) out.manager_of = lv.manager_of;
  if (kind === "person") out.person = lv.person ?? "";
  if (kind !== "roles" && lv.fallback_roles?.length) out.fallback_roles = lv.fallback_roles;
  if (kind === "roles" && (lv.quorum ?? 1) > 1) out.quorum = lv.quorum;
  if (lv.sla_days) out.sla_days = lv.sla_days;
  else if (lv.sla) out.sla = lv.sla;
  if (lv.escalate_to?.length) out.escalate_to = lv.escalate_to;
  return out;
}

export type SlaUnit = "hours" | "days";

/** A time limit as a number and a unit (working hours or working days), for a free entry. */
export function slaParts(x: { sla?: string | null; sla_days?: number | null }, fallback = "PT4H"): { n: number; unit: SlaUnit } {
  if (x.sla_days) return { n: x.sla_days, unit: "days" };
  const hours = isoHours(x.sla ?? fallback);
  return { n: hours ?? 4, unit: "hours" };
}

/** The fields for "decide within n working hours / days". */
export function slaFrom(n: number, unit: SlaUnit): { sla: string | null; sla_days: number | null } {
  const v = Math.max(1, Math.round(n));
  return unit === "days" ? { sla: null, sla_days: Math.min(v, MAX_WORKING_DAYS) } : { sla: `PT${v}H`, sla_days: null };
}

/** Hours in an ISO-8601 duration (PT4H, P1D = 24, PT90M = 1.5), or null. */
export function isoHours(iso: string): number | null {
  const m = /^P(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(iso);
  if (!m) return null;
  const [, d, h, mi, s] = m;
  return Number(d ?? 0) * 24 + Number(h ?? 0) + Number(mi ?? 0) / 60 + Number(s ?? 0) / 3600;
}

export type LevelIssue = "noRole" | "noPerson" | "quorumNotRoles" | "tooMany";

/** What stops a chain from being saved, per level (index from 0; -1 for the whole chain). */
export function levelIssues(levels: ApprovalLevel[]): { index: number; code: LevelIssue }[] {
  const out: { index: number; code: LevelIssue }[] = [];
  if (levels.length > MAX_LEVELS) out.push({ index: -1, code: "tooMany" });
  levels.forEach((lv, index) => {
    const kind = lv.approvers ?? "roles";
    if (kind === "roles" && !(lv.approver_roles ?? []).length) out.push({ index, code: "noRole" });
    if (kind === "person" && !(lv.person ?? "").trim()) out.push({ index, code: "noPerson" });
    if (kind !== "roles" && (lv.quorum ?? 1) > 1) out.push({ index, code: "quorumNotRoles" });
  });
  return out;
}

/** Move one item of a list up (-1) or down (+1). */
export function moved<T>(list: readonly T[], index: number, by: -1 | 1): T[] {
  const to = index + by;
  if (to < 0 || to >= list.length) return [...list];
  const out = [...list];
  const [item] = out.splice(index, 1);
  out.splice(to, 0, item as T);
  return out;
}

/** Roles to offer for "who approves": common ones, the organisation's own, and any in use. */
export function approverRoleChoices(picker: readonly { name: string; area: string }[] | null | undefined, inUse: readonly string[] = []): string[] {
  const own = (picker ?? []).filter((r) => r.area === "work" || r.area === "organisation").map((r) => r.name);
  return [...new Set([...COMMON_APPROVER_ROLES, ...own, ...inUse])];
}
